/**
 * PlansUpgradeModal — the premium plans comparison modal (Stage C).
 *
 * The conversion moment: 4 purchasable tiers side by side in a clean
 * comparison table (price, quotas, features), the shop's CURRENT plan
 * highlighted, "Most popular" on Scale, an Upgrade/Choose CTA per tier
 * that starts the Shopify subscription flow, plus a "Custom plan"
 * contact column (mailto autonoma@ontechnologiesptyltd.com).
 *
 * Shown from: Settings → Plan, clicking any locked feature, or hitting
 * a quota / changes-budget limit (deep-linked via `highlight` to pulse
 * the relevant row). Mounted once app-wide via <PlansModalHost /> in
 * AppLayout and opened with openPlansModal() from anywhere.
 *
 * Tier numbers come LIVE from GET /api/billing/plans (derived from
 * plan-quotas.config.js on the backend) so this UI can never drift
 * from what's enforced.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from '@shopify/polaris';
import useBilling from '../hooks/useBilling';
import useShopify from '../hooks/useShopify';
import { PLANS_MODAL_EVENT } from '../utils/plansModal';
import { apiFetch } from '../lib/api';

const CONTACT_EMAIL = 'autonoma@ontechnologiesptyltd.com';

// Comparison rows: label + how to read the value off a plan's display
// features. `highlightKeys` ties backend error `feature` codes to rows.
const ROWS = [
  { label: 'AI videos / month', keys: ['videoAds'], value: (f) => (f.videoAds ? num(f.videosPerMonth) : false) },
  { label: 'Image ads / month', keys: ['imageAds'], value: (f) => num(f.imageAdsPerMonth) },
  { label: 'Full product builds / month', keys: ['productBuilds'], value: (f) => num(f.productsPerMonth) },
  { label: 'Advertorials / month', keys: ['advertorials'], value: (f) => num(f.advertorialsPerMonth) },
  { label: 'Find-products searches / month', keys: ['findProductsSearches'], value: (f) => num(f.findProductsPerMonth) },
  { label: 'Ad copy runs / month', keys: ['adCopy'], value: (f) => num(f.adCopyRunsPerMonth) },
  { label: 'Upsell searches / month', keys: ['upsells'], value: (f) => (f.upsells ? num(f.upsellSearchesPerMonth) : false) },
  { label: 'Split tests / month', keys: ['splitTests'], value: (f) => (f.splitTests ? num(f.splitTestsPerMonth) : false) },
  { label: 'Website builder', keys: [], value: (f) => !!f.websiteBuilder },
  // NOTE: the changes budget is deliberately NOT a row here — it's an
  // internal cost-control mechanic (still fully enforced + visible in
  // Settings → usage), not a merchant-facing headline feature.
  { label: 'AI chat (does tasks for you)', keys: ['aiChat'], value: (f) => !!f.aiChat },
  { label: 'Publish ads to Meta', keys: ['publishAds', 'adsManager'], value: (f) => !!f.publishAds },
  { label: 'Portrait feed ads (4:5)', keys: ['portraitPlacements'], value: (f) => !!f.portraitPlacements },
  { label: 'Mobile app', keys: ['mobileApp'], value: (f) => !!f.mobileApp },
];

function num(v) {
  if (v === 'unlimited' || v === Infinity) return 'Unlimited';
  return Number(v).toLocaleString();
}

/**
 * App-wide host: listens for openPlansModal() events and renders the
 * modal. Mount once (AppLayout).
 */
export function PlansModalHost() {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(null);

  useEffect(() => {
    const onOpen = (e) => {
      setHighlight(e.detail?.highlight || null);
      setOpen(true);
    };
    window.addEventListener(PLANS_MODAL_EVENT, onOpen);
    return () => window.removeEventListener(PLANS_MODAL_EVENT, onOpen);
  }, []);

  if (!open) return null;
  return <PlansUpgradeModal highlight={highlight} onClose={() => setOpen(false)} />;
}

export default function PlansUpgradeModal({ onClose, highlight = null }) {
  const { shop } = useShopify();
  const { plan: currentPlan, subscribe, trialUsed } = useBilling();
  const [plans, setPlans] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    apiFetch(`/api/billing/plans`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (alive && d?.plans) setPlans(d.plans); })
      .catch(() => { if (alive) setError('Could not load plans — please try again.'); });
    return () => { alive = false; };
  }, []);

  const tierKeys = useMemo(() => (plans ? Object.keys(plans) : []), [plans]);
  // Legacy plan keys still highlight their modern column.
  const currentKey = { standard: 'starter', pro: 'scale' }[currentPlan] || currentPlan;

  const handleChoose = async (planKey) => {
    setError(null);
    setPendingPlan(planKey);
    try {
      const { confirmationUrl } = await subscribe(planKey);
      if (!confirmationUrl) throw new Error('No confirmation URL returned');
      // Top-frame redirect — Shopify's billing page can't load in the iframe.
      if (window.top && window.top !== window.self) window.top.location.href = confirmationUrl;
      else window.location.href = confirmationUrl;
    } catch (err) {
      setError(err.message || 'Could not start the upgrade — please try again.');
      setPendingPlan(null);
    }
  };

  const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Custom Plan Enquiry — ${shop || 'my store'}`)}`;

  return createPortal(
    <div className="au-plans-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Compare plans">
      <div className="au-plans-modal" onClick={(e) => e.stopPropagation()}>
        <div className="au-plans-modal__header">
          <div>
            <div className="au-plans-modal__title">Choose your plan</div>
            <div className="au-plans-modal__sub">
              Every plan includes the full AI store builder · cancel anytime
            </div>
          </div>
          <button type="button" className="au-plans-modal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {error && <div className="au-plans-modal__error" role="alert">{error}</div>}

        {!plans ? (
          <div className="au-plans-modal__loading">
            <Spinner accessibilityLabel="Loading plans" size="large" />
          </div>
        ) : (
          <div className="au-plans-modal__scroller">
            <table className="au-plans-table">
              <thead>
                <tr>
                  <th className="au-plans-table__feature-col" aria-label="Feature" />
                  {tierKeys.map((key) => {
                    const p = plans[key];
                    const isCurrent = key === currentKey;
                    return (
                      <th key={key} className={`au-plans-table__plan ${isCurrent ? 'is-current' : ''} ${p.recommended ? 'is-recommended' : ''}`}>
                        {p.recommended && <span className="au-plans-table__badge">Most popular</span>}
                        {isCurrent && <span className="au-plans-table__badge au-plans-table__badge--current">Current plan</span>}
                        <div className="au-plans-table__name">{p.name}</div>
                        <div className="au-plans-table__price">
                          <span className="au-plans-table__amount">${p.price}</span>
                          <span className="au-plans-table__cadence">/mo</span>
                        </div>
                        {/* Trial badge only where one actually applies:
                            Starter/Growth carry a 3-day trial, Scale/
                            Enterprise are billed immediately — and once
                            the store has used its one trial, every plan
                            starts paid. */}
                        <div className="au-plans-table__trial">
                          {p.trialDays > 0 && !trialUsed ? `${p.trialDays}-day free trial` : 'Billed immediately'}
                        </div>
                        <button
                          type="button"
                          className={`au-plans-table__cta ${p.recommended ? 'au-plans-table__cta--accent' : ''}`}
                          disabled={!!pendingPlan || isCurrent}
                          onClick={() => handleChoose(key)}
                        >
                          {isCurrent ? 'Your plan'
                            : pendingPlan === key ? 'Redirecting…'
                            : currentKey && tierKeys.indexOf(currentKey) > tierKeys.indexOf(key) ? 'Choose plan'
                            : 'Upgrade'}
                        </button>
                      </th>
                    );
                  })}
                  <th className="au-plans-table__plan au-plans-table__plan--custom">
                    <div className="au-plans-table__name">Custom</div>
                    <div className="au-plans-table__price">
                      <span className="au-plans-table__amount" style={{ fontSize: 20 }}>Need more?</span>
                    </div>
                    <div className="au-plans-table__trial">Tailored allowances &amp; pricing</div>
                    <a className="au-plans-table__cta au-plans-table__cta--ghost" href={mailto}>
                      Contact us
                    </a>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => {
                  const hot = highlight && row.keys.includes(highlight);
                  return (
                    <tr key={row.label} className={hot ? 'is-highlighted' : ''}>
                      <td className="au-plans-table__feature-col">{row.label}</td>
                      {tierKeys.map((key) => {
                        const v = row.value(plans[key].features || {});
                        return (
                          <td key={key} className={key === currentKey ? 'is-current' : ''}>
                            {v === true ? <span className="au-plans-table__yes">✓</span>
                              : v === false || v === '0' || v === 0 ? <span className="au-plans-table__no">—</span>
                              : <span className="au-plans-table__val">{v}</span>}
                          </td>
                        );
                      })}
                      <td><span className="au-plans-table__val au-plans-table__val--custom">Custom</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="au-plans-modal__footer">
          You'll approve the recurring charge on Shopify before any money is taken.
          Need something bigger? <a href={mailto}>Talk to us about a custom plan</a>.
        </div>
      </div>
    </div>,
    document.body,
  );
}
